import React, { useEffect, useRef, useState } from "react";
import "./InteractiveCameraLocationMap.css";

type PositionSource = "click" | "drag";

type Props = {
  latitude: number;
  longitude: number;
  cameraName?: string;
  disabled?: boolean;
  onPositionChange: (
    latitude: number,
    longitude: number,
    source: PositionSource,
  ) => void;
};

type LeafletApi = any;

declare global {
  interface Window {
    L?: LeafletApi;
    __qumraLeafletPromise?: Promise<LeafletApi>;
  }
}

const LEAFLET_CSS_ID = "qumra-leaflet-css";
const LEAFLET_SCRIPT_ID = "qumra-leaflet-script";
const LEAFLET_CSS_URL = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
const LEAFLET_SCRIPT_URL = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
const LEAFLET_CSS_INTEGRITY =
  "sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=";
const LEAFLET_SCRIPT_INTEGRITY =
  "sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=";
const DEFAULT_TILE_URL = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
const TILE_URL = process.env.REACT_APP_CAMERA_MAP_TILE_URL || DEFAULT_TILE_URL;

function loadLeaflet() {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.reject(new Error("Interactive map is not available."));
  }

  if (window.L) return Promise.resolve(window.L);
  if (window.__qumraLeafletPromise) return window.__qumraLeafletPromise;

  window.__qumraLeafletPromise = new Promise<LeafletApi>((resolve, reject) => {
    if (!document.getElementById(LEAFLET_CSS_ID)) {
      const stylesheet = document.createElement("link");
      stylesheet.id = LEAFLET_CSS_ID;
      stylesheet.rel = "stylesheet";
      stylesheet.href = LEAFLET_CSS_URL;
      stylesheet.integrity = LEAFLET_CSS_INTEGRITY;
      stylesheet.crossOrigin = "anonymous";
      document.head.appendChild(stylesheet);
    }

    const resolveLeaflet = () => {
      if (window.L) resolve(window.L);
      else reject(new Error("Interactive map library did not initialize."));
    };

    const rejectLeaflet = () => {
      window.__qumraLeafletPromise = undefined;
      reject(new Error("Interactive map could not be loaded."));
    };

    const existing = document.getElementById(
      LEAFLET_SCRIPT_ID,
    ) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener("load", resolveLeaflet, { once: true });
      existing.addEventListener("error", rejectLeaflet, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = LEAFLET_SCRIPT_ID;
    script.src = LEAFLET_SCRIPT_URL;
    script.integrity = LEAFLET_SCRIPT_INTEGRITY;
    script.crossOrigin = "anonymous";
    script.async = true;
    script.addEventListener("load", resolveLeaflet, { once: true });
    script.addEventListener("error", rejectLeaflet, { once: true });
    document.head.appendChild(script);
  });

  return window.__qumraLeafletPromise;
}

function validPosition(latitude: number, longitude: number) {
  return (
    Number.isFinite(latitude) &&
    latitude >= -90 &&
    latitude <= 90 &&
    Number.isFinite(longitude) &&
    longitude >= -180 &&
    longitude <= 180
  );
}

function mapLink(latitude: number, longitude: number) {
  return `https://www.openstreetmap.org/?mlat=${encodeURIComponent(
    String(latitude),
  )}&mlon=${encodeURIComponent(String(longitude))}#map=19/${encodeURIComponent(
    String(latitude),
  )}/${encodeURIComponent(String(longitude))}`;
}

export default function InteractiveCameraLocationMap({
  latitude,
  longitude,
  cameraName,
  disabled = false,
  onPositionChange,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const positionRef = useRef({ latitude, longitude });
  const disabledRef = useRef(disabled);
  const onPositionChangeRef = useRef(onPositionChange);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState("");

  positionRef.current = { latitude, longitude };
  disabledRef.current = disabled;
  onPositionChangeRef.current = onPositionChange;

  useEffect(() => {
    let cancelled = false;

    void loadLeaflet()
      .then((L) => {
        if (cancelled || !containerRef.current || mapRef.current) return;

        const current = positionRef.current;
        if (!validPosition(current.latitude, current.longitude)) return;

        const map = L.map(containerRef.current, {
          zoomControl: true,
          attributionControl: true,
        }).setView([current.latitude, current.longitude], 19);

        L.tileLayer(TILE_URL, {
          minZoom: 2,
          maxZoom: 19,
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> contributors',
        }).addTo(map);

        const pinIcon = L.divIcon({
          className: "qumra-location-marker-host",
          html: '<div class="qumra-location-marker" aria-hidden="true"><span></span></div>',
          iconSize: [36, 46],
          iconAnchor: [18, 43],
        });

        const marker = L.marker([current.latitude, current.longitude], {
          draggable: !disabledRef.current,
          icon: pinIcon,
          keyboard: true,
          title: cameraName || "Camera location",
          alt: cameraName ? `${cameraName} location pin` : "Camera location pin",
          autoPan: true,
        }).addTo(map);

        const commitPosition = (nextLatitude: number, nextLongitude: number, source: PositionSource) => {
          if (disabledRef.current || !validPosition(nextLatitude, nextLongitude)) return;
          marker.setLatLng([nextLatitude, nextLongitude]);
          onPositionChangeRef.current(nextLatitude, nextLongitude, source);
        };

        marker.on("dragend", () => {
          const next = marker.getLatLng();
          commitPosition(next.lat, next.lng, "drag");
        });

        map.on("click", (event: any) => {
          commitPosition(event.latlng.lat, event.latlng.lng, "click");
        });

        mapRef.current = map;
        markerRef.current = marker;
        setLoadError("");
        setReady(true);
        window.setTimeout(() => map.invalidateSize(), 0);
      })
      .catch((error) => {
        if (cancelled) return;
        setLoadError(
          error instanceof Error
            ? error.message
            : "Interactive map could not be loaded.",
        );
      });

    return () => {
      cancelled = true;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markerRef.current = null;
      }
    };
  }, [cameraName]);

  useEffect(() => {
    if (!ready || !mapRef.current || !markerRef.current) return;
    if (!validPosition(latitude, longitude)) return;

    const markerPosition = markerRef.current.getLatLng();
    const changed =
      Math.abs(markerPosition.lat - latitude) > 0.0000001 ||
      Math.abs(markerPosition.lng - longitude) > 0.0000001;

    if (changed) {
      markerRef.current.setLatLng([latitude, longitude]);
      mapRef.current.panTo([latitude, longitude], { animate: false });
    }
  }, [latitude, longitude, ready]);

  useEffect(() => {
    if (!markerRef.current?.dragging) return;
    if (disabled) markerRef.current.dragging.disable();
    else markerRef.current.dragging.enable();
  }, [disabled, ready]);

  return (
    <div className="camera-location-interactive-shell">
      <div
        ref={containerRef}
        className="camera-location-interactive-map"
        aria-label={`Interactive map for ${cameraName || "camera"}`}
      />

      {!ready && !loadError && (
        <div className="camera-location-map-loading" role="status">
          Loading interactive map…
        </div>
      )}

      {loadError && (
        <div className="camera-location-map-loading error" role="alert">
          <strong>Interactive map unavailable</strong>
          <span>{loadError} You can still enter exact coordinates manually.</span>
        </div>
      )}

      <div className="camera-location-map-drag-hint">
        <strong>Fine-tune the camera position</strong>
        <span>Drag the pin or click anywhere on the map. Zoom in for maximum precision.</span>
      </div>

      <div className="camera-location-map-footer">
        <div>
          <strong>Exact pin</strong>
          <span>
            {latitude.toFixed(7)}, {longitude.toFixed(7)}
          </span>
        </div>
        <a href={mapLink(latitude, longitude)} target="_blank" rel="noreferrer">
          Open map ↗
        </a>
      </div>
    </div>
  );
}
